import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/** Active kiosk session — populated when staff enters kiosk mode. */
export interface ActiveKiosk {
  accountId: string;
  /**
   * The dealer's own name, shown in the kiosk top bar and as the screensaver's
   * identity line. Comes from `useSession().shopName` at entry.
   *
   * Was `locationName`, alongside a `locationId` that `EnterKioskModal` always
   * set to `''` (vendor#431). Both were storefront fork leftovers: this repo
   * has no `locations` table and no locations model — scoping is `account_id`
   * and nothing else. Renamed rather than kept, because a `location`-named
   * field in a kiosk session reads like scope and is not.
   */
  dealerName: string;
  enteredAt: number; // Date.now() at entry
}

interface KioskState {
  /**
   * Trade-in credit as a percentage of sell price shown to customers.
   *
   * UNUSED at runtime (kept for backward-compat with any already-persisted
   * localStorage value): the live trade rate the kiosk wishlist drawer reads
   * is `accounts.kiosk_trade_pct` via `useKioskAccountSettings`.
   * Do not wire a new reader to this field; remove it once no persisted
   * client carries a stale copy worth preserving.
   */
  tradeValuePercent: number;

  /** Active session (null = not in kiosk mode). */
  activeKiosk: ActiveKiosk | null;

  setTradeValuePercent: (pct: number) => void;
  enterKiosk: (session: ActiveKiosk) => void;
  exitKiosk: () => void;
}

/**
 * Kiosk-mode store: which account is in kiosk mode, and under whose name.
 *
 * Shell: used by both shells/desktop/ (EnterKioskModal) and shells/kiosk/.
 * It is safe to import from either shell because it holds no staff-scoped
 * Supabase data — only the dealer name chosen at entry.
 *
 * **The PIN is no longer here, and that is the entire point of vendor#365.**
 * This store used to carry `defaultPin: '1234'` plus a `pin` on the session,
 * persisted in plain text to `localStorage`, which `ExitKioskModal` then
 * compared *in the browser*. So the boundary between an anonymous customer
 * browse and the whole staff app was a string the page checking it also held.
 * It also carried `pinFailCount` / `pinLockedUntil`, which meant the lockout
 * was defeated by clearing site data.
 *
 * All five moved to the server: `kiosk_security` holds a bcrypt hash and the
 * failure counters, reachable only through `verify_kiosk_pin` /
 * `set_kiosk_pin`. See `features/settings/hooks/useKioskSecurity.ts`.
 * **Do not reintroduce a PIN field here**, not as a cache and not as an
 * offline fallback — an offline fallback is a bypass with a nicer name.
 */
/** The `localStorage` key the kiosk store persists under. Storefront-era name, kept so
 * a device already in kiosk mode stays locked across the upgrade. */
export const KIOSK_STORAGE_KEY = 'mtb-storefront-kiosk';

export const useKiosk = create<KioskState>()(
  persist(
    (set) => ({
      tradeValuePercent: 75,
      activeKiosk: null,

      setTradeValuePercent: (pct) => set({ tradeValuePercent: Math.max(0, Math.min(100, pct)) }),
      enterKiosk: (session) => set({ activeKiosk: session }),
      exitKiosk: () => set({ activeKiosk: null }),
    }),
    {
      name: KIOSK_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      /**
       * Persisted clients carry the retired plaintext `defaultPin`, `pin`,
       * `pinChangedAt`, `pinFailCount` and `pinLockedUntil` keys. Zustand
       * merges persisted state over the initial state, so without this they
       * would survive in `localStorage` — and a plaintext PIN left on a tablet
       * is exactly what vendor#365 set out to remove. Dropping unknown keys on
       * rehydrate means the next write of this store erases them.
       */
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<KioskState>;
        return {
          ...current,
          tradeValuePercent:
            typeof p.tradeValuePercent === 'number'
              ? p.tradeValuePercent
              : current.tradeValuePercent,
          activeKiosk: p.activeKiosk
            ? {
                accountId: p.activeKiosk.accountId,
                dealerName: p.activeKiosk.dealerName,
                enteredAt: p.activeKiosk.enteredAt,
              }
            : null,
        };
      },
    },
  ),
);

/**
 * Cross-tab sync (vendor#1428): every open tab follows this device's kiosk state.
 *
 * Zustand's `persist` writes `localStorage` but never listens for other tabs'
 * writes. So when kiosk mode was entered in one tab, a second tab already open
 * stayed an unlocked staff app until reloaded. A customer could reach it through
 * the tab switcher, on the live staff session (vendor#742), with no PIN.
 * `KioskLockGuard` reads this store, so rehydrating it is what locks that tab.
 *
 * Why the `storage` event and not a `BroadcastChannel`: `localStorage` is
 * already the source of truth, and the browser fires `storage` in every other
 * same-origin tab on each write `persist` makes, for both enter and exit. A
 * channel would be a second copy of the same fact, with its own way to drift
 * (a message sent before a listener existed, a writer that forgets to post).
 *
 * Exit propagates too: an exit in another tab unlocks this one. That exit
 * already passed the PIN on this device, which is the lock's whole contract.
 * Only a WRITE of this store's own key is followed. `persist` only ever
 * writes the key and never removes it, so a removal (`newValue === null`) or a
 * `localStorage.clear()` elsewhere (`key === null`) cannot come from an exit.
 * Both are ignored, so wiping storage in another tab never unlocks this one.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KIOSK_STORAGE_KEY && e.newValue !== null) void useKiosk.persist.rehydrate();
  });
}
